/**
 * Igloo (vanilla igloo/top + bottom templates): a snow dome with a short
 * entrance tunnel, ice windows, a bed, furnace and crafting table, and — half
 * the time — a trapdoor-and-ladder shaft (a hole here) down to a stone-brick
 * basement lab with a chest.
 */

import { SEA_LEVEL } from '../../constants';
import { AIR, WOOL_IDS } from '../../blocks';
import { Biome } from '../biomes';
import type { TerrainGenerator } from '../terrain';
import {
  block, blueprint, type Builder, cell, chance, F, int, centredPiece, type Piece, type Rng, seedOf, type StructureType,
} from './kit';

const SNOW = block('Snow Block');

// The basement: 7 x 7 of stone bricks, the shaft arriving in the front row
// and the lab bench along the back wall.
const BASEMENT = blueprint({
  S: cell.mix([[block('Stone Bricks'), 4], [block('Mossy Stone Bricks'), 1], [block('Cracked Stone Bricks'), 1]]),
  C: cell.chest(F.NV), T: block('Crafting Table'), U: cell.furnace(F.NV), '*': block('Torch'),
}, [
  ['SSSSSSS', 'SSSSSSS', 'SSSSSSS', 'SSSSSSS', 'SSSSSSS', 'SSSSSSS', 'SSSSSSS'],
  ['SSS.SSS', 'S.....S', 'S.....S', 'S.....S', 'S.....S', 'STU.C.S', 'SSSSSSS'],
  ['SSS.SSS', 'S.....S', 'S.....S', 'S.....S', 'S.....S', 'S*...*S', 'SSSSSSS'],
  ['SSS.SSS', 'S.....S', 'S.....S', 'S.....S', 'S.....S', 'S.....S', 'SSSSSSS'],
  ['SSS.SSS', 'SSSSSSS', 'SSSSSSS', 'SSSSSSS', 'SSSSSSS', 'SSSSSSS', 'SSSSSSS'],
]);

function buildIgloo(b: Builder): void {
  const y = b.y;
  b.fill(-1, y + 1, -1, 7, y + 6, 8, AIR);
  // the dome: an ellipsoid shell over a round floor, carpeted inside
  for (let v = 1; v < 8; v++) {
    for (let u = 0; u < 7; u++) {
      const r2 = (u - 3) * (u - 3) + (v - 4) * (v - 4);
      for (let dy = 0; dy <= 4; dy++) {
        const d = Math.sqrt(r2 + dy * dy * 1.3);
        if (d <= 3.6) b.set(u, y + 1 + dy, v, d > 2.6 ? SNOW : AIR);
      }
      if (r2 <= 13) b.set(u, y, v, r2 <= 7 ? WOOL_IDS.white : SNOW);
    }
  }
  // entrance tunnel at the front
  b.fill(2, y + 1, 0, 4, y + 2, 1, SNOW);
  b.fill(3, y + 1, 0, 3, y + 2, 2, AIR);
  b.fill(3, y + 3, 0, 3, y + 3, 1, SNOW);
  b.set(0, y + 2, 4, block('Ice')); b.set(6, y + 2, 4, block('Ice'));
  // furniture
  b.furnace(1, y + 1, 5, F.PU);
  b.set(1, y + 1, 4, block('Crafting Table'));
  b.bed(5, y + 1, 5, F.PV);
  b.set(3, y + 1, 6, block('Spruce Planks'));
  b.set(3, y + 2, 6, block('Torch'));
  // half the igloos hide a lab: a shaft under the carpet down to the basement
  if (chance(b.rnd, 0.5)) {
    const base = y - 12;
    b.fill(3, base + 4, 4, 3, y, 4, AIR);
    b.stamp(BASEMENT, 0, base, 4); // its shaft column (3, 0) lands under (3, 4)
  }
}

function igloo(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  const snowy = c.biome === Biome.SnowyPlains || c.biome === Biome.SnowyTaiga || c.biome === Biome.SnowySlopes;
  if (!snowy || c.height < SEA_LEVEL + 1) return null;
  return [centredPiece(x, z, int(rnd, 4), 7, 8, c.height, true, seedOf(rnd), buildIgloo)];
}

export const IGLOO: StructureType = {
  name: 'Igloo', spacing: 32, separation: 8, salt: 14357618, reach: 1, generate: igloo,
};
